// On-screen number pad with a display line. Shared by the math games.

import { h } from '@kit/dom';

export interface Numpad { el: HTMLElement; value(): string; clear(): void; setEnabled(on: boolean): void }

export function makeNumpad(opts: { onEnter(v: number): void; enterLabel?: string; allowDecimal?: boolean; onChange?(v: string): void }): Numpad {
  let val = '';
  const display = h('div.prompt', { style: { minHeight: '44px', letterSpacing: '2px' } }, '_');
  const buttons: HTMLButtonElement[] = [];
  const key = (label: string, fn: () => void, extra = '') => {
    const b = h('button.key' + extra, { onclick: fn }, label) as HTMLButtonElement;
    buttons.push(b);
    return b;
  };
  const set = (v: string) => { val = v; display.textContent = val || '_'; opts.onChange?.(val); };
  const pad = h('div.numpad', null,
    ...['7', '8', '9', '4', '5', '6', '1', '2', '3'].map(d => key(d, () => { if (val.length < 7) set(val + d); })),
    key(opts.allowDecimal ? '.' : '⌫', () => { if (opts.allowDecimal) { if (!val.includes('.') && val.length < 6) set((val || '0') + '.'); } else set(val.slice(0, -1)); }),
    key('0', () => { if (val.length < 7) set(val + '0'); }),
    key(opts.allowDecimal ? '⌫' : (opts.enterLabel || 'Go'), () => { if (opts.allowDecimal) set(val.slice(0, -1)); else submit(); }, opts.allowDecimal ? '' : '.good'),
  );
  const enter = opts.allowDecimal ? h('button.btn.primary.block', { onclick: submit }, opts.enterLabel || 'Go') : null;
  function submit() { if (val === '' || val === '.') return; const n = Number(val); if (Number.isFinite(n)) opts.onEnter(n); }
  const el = h('div.stack', { style: { gap: '6px' } }, display, pad, enter);
  return { el, value: () => val, clear: () => set(''), setEnabled(on) { for (const b of buttons) b.disabled = !on; if (enter) (enter as HTMLButtonElement).disabled = !on; } };
}

/** 4 big choice buttons; returns the container and a way to lock them */
export function makeChoices(choices: number[] | string[], onPick: (v: number | string, btn: HTMLButtonElement) => void): { el: HTMLElement; lock(): void; reset(): void } {
  const btns: HTMLButtonElement[] = [];
  const el = h('div.choices', null, ...choices.map(c => {
    const b = h('button.choice', { onclick: () => onPick(c, b) }, String(c)) as HTMLButtonElement;
    btns.push(b);
    return b;
  }));
  return { el, lock() { for (const b of btns) b.disabled = true; }, reset() { for (const b of btns) { b.disabled = false; b.className = 'choice'; } } };
}

export function nearbyChoices(answer: number, rng: () => number, spread = 3, count = 4): number[] {
  const set = new Set<number>([answer]);
  let guard = 0;
  while (set.size < count && guard++ < 100) {
    const d = Math.floor(rng() * spread * 2 + 1) - spread;
    const v = answer + (d === 0 ? spread + 1 : d);
    if (v >= 0) set.add(v);
  }
  const arr = [...set];
  for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  return arr;
}
